import { applyRules, fleschKincaid } from '../../industry-packs/types'
import { getPacksForIndustry } from '../../industry-packs/registry'
import type { PackFinding } from '../../industry-packs/types'

export interface ComplianceCheckInput {
  stimulusText: string
  industry: string
  segmentId?: string
  stimulusId?: string
}

export interface ComplianceCheckResult {
  pass_template: 'INDUSTRY_COMPLIANCE_CHECK'
  industry: string
  pack_ids: string[]
  findings: PackFinding[]
  max_severity: 'info' | 'warn' | 'block' | 'clean'
  plain_language_score: number
  summary: string
}

export function runIndustryComplianceCheck(input: ComplianceCheckInput): ComplianceCheckResult {
  const packs = getPacksForIndustry(input.industry)
  const allFindings: PackFinding[] = []

  for (const pack of packs) {
    const findings = applyRules(input.stimulusText, pack.rules)
    allFindings.push(...findings)
  }

  const fkGrade = fleschKincaid(input.stimulusText)
  // Plain language score: invert FK grade to 0-100 scale (grade 6 = 100, grade 16+ = 0)
  const plainLanguageScore = Math.round(Math.max(0, Math.min(100, (16 - fkGrade) / 10 * 100)))

  const maxSeverity: 'info' | 'warn' | 'block' | 'clean' =
    allFindings.some(f => f.severity === 'block') ? 'block'
    : allFindings.some(f => f.severity === 'warn') ? 'warn'
    : allFindings.some(f => f.severity === 'info') ? 'info'
    : 'clean'

  const summary = maxSeverity === 'clean'
    ? `No compliance findings. Plain language score: ${plainLanguageScore}/100.`
    : `${allFindings.length} finding${allFindings.length !== 1 ? 's' : ''} (${maxSeverity === 'block' ? 'blocking' : allFindings.filter(f => f.severity === 'warn').length + ' warnings'}). Plain language score: ${plainLanguageScore}/100.`

  return {
    pass_template: 'INDUSTRY_COMPLIANCE_CHECK',
    industry: input.industry,
    pack_ids: packs.map(p => p.pack_id),
    findings: allFindings,
    max_severity: maxSeverity,
    plain_language_score: plainLanguageScore,
    summary,
  }
}
